import { useCallback, useEffect, useState } from "react";
import {
  MarketDataFill,
  MarketDataGaps,
  MarketDataQuality,
  MarketDataSeries,
  api,
  isError,
} from "../api";

/** Stored price history (G11): what is stored, filling date ranges from
 * MT5, importing CSVs, and checking a series for holes. Backtests read
 * these through the "Stored history" data source. */

const TIMEFRAMES = ["M1", "M5", "M15", "M30", "H1", "H4", "D1"];

function isoDate(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

const input = "bg-page border border-border rounded px-2 py-1 font-mono text-xs";
const button =
  "px-3 py-1 rounded border border-primary text-primary text-xs uppercase font-bold disabled:opacity-50";

export function MarketDataPanel() {
  const [series, setSeries] = useState<MarketDataSeries[]>([]);
  const [fills, setFills] = useState<MarketDataFill[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [gaps, setGaps] = useState<MarketDataGaps | null>(null);
  const [quality, setQuality] = useState<MarketDataQuality | null>(null);

  const [symbol, setSymbol] = useState("EURUSD");
  const [timeframe, setTimeframe] = useState("M1");
  const [start, setStart] = useState(() => isoDate(-365));
  const [end, setEnd] = useState(() => isoDate(1));
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [cov, fl] = await Promise.all([api.marketDataCoverage(), api.marketDataFills()]);
    if (!isError(cov)) setSeries(cov.series);
    if (!isError(fl)) setFills(fl.fills);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll while a fill is running; stop once none are.
  const running = fills.some((f) => f.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void refresh(), 2000);
    return () => clearInterval(t);
  }, [running, refresh]);

  async function onFill() {
    if (end <= start) {
      setMessage("The end date must be after the start.");
      return;
    }
    setBusy(true);
    const r = await api.marketDataFill({
      symbol,
      timeframe,
      start: `${start}T00:00:00Z`,
      end: `${end}T00:00:00Z`,
    });
    setBusy(false);
    if (isError(r)) {
      setMessage(`Fill failed: ${r.error}`);
      return;
    }
    setMessage(`Filling ${r.symbol} ${r.timeframe} from MT5 in ${r.id}…`);
    await refresh();
  }

  async function onImport() {
    if (!file) {
      setMessage("Choose a CSV to import.");
      return;
    }
    setBusy(true);
    setMessage(`Importing ${file.name}…`);
    const r = await api.marketDataImport(file, symbol, timeframe);
    setBusy(false);
    if (isError(r)) {
      setMessage(`Import failed: ${r.error}`);
      return;
    }
    setMessage(
      `Imported ${r.inserted.toLocaleString()} new bars (${r.already_stored.toLocaleString()} ` +
        `already stored), ${r.first?.slice(0, 10)} → ${r.last?.slice(0, 10)}.`,
    );
    await refresh();
  }

  async function onQuality(s: MarketDataSeries) {
    setBusy(true);
    setMessage(`Checking ${s.symbol} ${s.timeframe} for bad bars…`);
    const r = await api.marketDataQuality(s.symbol, s.timeframe);
    setBusy(false);
    if (isError(r)) {
      setMessage(`Quality check failed: ${r.error}`);
      return;
    }
    setMessage(null);
    setQuality(r);
  }

  async function onClean(q: MarketDataQuality) {
    setBusy(true);
    const r = await api.marketDataClean(q.symbol, q.timeframe);
    setBusy(false);
    if (isError(r)) {
      setMessage(`Clean failed: ${r.error}`);
      return;
    }
    setMessage(`Excluded ${r.excluded} bar(s) from ${q.symbol} ${q.timeframe}; each fix is logged.`);
    setQuality(null);
    await refresh();
  }

  async function onRestore(q: MarketDataQuality) {
    setBusy(true);
    const r = await api.marketDataRestore(q.symbol, q.timeframe);
    setBusy(false);
    if (isError(r)) {
      setMessage(`Restore failed: ${r.error}`);
      return;
    }
    setMessage(`Restored ${r.restored} bar(s) in ${q.symbol} ${q.timeframe}.`);
    setQuality(null);
    await refresh();
  }

  async function onGaps(s: MarketDataSeries) {
    const r = await api.marketDataGaps(s.symbol, s.timeframe);
    if (isError(r)) {
      setMessage(`Gap check failed: ${r.error}`);
      return;
    }
    setGaps(r);
  }

  return (
    <section className="bg-surface border border-border rounded p-4 flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-bold uppercase tracking-wide">Market data</h2>
        <span className="text-[10px] text-fg-muted">
          stored history for multi-year backtests · pick "Stored history" as the data source
          above
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="uppercase text-fg-muted">Symbol</span>
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            className={`${input} w-28`}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="uppercase text-fg-muted">Timeframe</span>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)} className={input}>
            {TIMEFRAMES.map((tf) => (
              <option key={tf}>{tf}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="uppercase text-fg-muted">From</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={input} />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="uppercase text-fg-muted">To (exclusive)</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={input} />
        </label>
        <button onClick={onFill} disabled={busy} className={button}>
          Fill from MT5
        </button>
        <span className="text-fg-muted text-xs">or</span>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="text-xs text-fg-muted max-w-[14rem]"
        />
        <button onClick={onImport} disabled={busy || !file} className={button}>
          Import CSV
        </button>
      </div>
      <p className="text-[10px] text-fg-muted -mt-2">
        Fills only add bars that are missing, so re-running a range is safe. How far back MT5
        goes depends on the broker; M1 usually reaches furthest. A CSV import needs
        timestamp,open,high,low,close (optional volume, spread_pips) with UTC open times.
      </p>

      {message && <p className="text-xs font-mono">{message}</p>}

      {fills.length > 0 && (
        <div className="flex flex-col gap-1">
          <h3 className="text-xs uppercase text-fg-muted">Fills</h3>
          <ul className="text-xs font-mono flex flex-col gap-1">
            {fills.slice(0, 8).map((f) => (
              <li key={f.id} className="flex flex-wrap gap-x-3">
                <span>
                  {f.symbol} {f.timeframe} {f.start.slice(0, 10)} → {f.end.slice(0, 10)}
                </span>
                <span
                  className={
                    f.status === "failed"
                      ? "text-danger"
                      : f.status === "completed"
                        ? "text-success"
                        : "text-warn"
                  }
                >
                  {f.status}
                  {f.status === "running" && f.chunks_total > 0
                    ? ` ${Math.round((100 * f.chunks_done) / f.chunks_total)}%`
                    : ""}
                </span>
                <span className="text-fg-muted">
                  {f.inserted.toLocaleString()} new of {f.fetched.toLocaleString()} fetched
                </span>
                {f.error && <span className="text-danger">{f.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h3 className="text-xs uppercase text-fg-muted">Stored</h3>
        {series.length === 0 ? (
          <p className="text-xs text-fg-muted">Nothing stored yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs font-mono w-full">
              <thead className="text-fg-muted text-left">
                <tr>
                  <th className="pr-4">Symbol</th>
                  <th className="pr-4">TF</th>
                  <th className="pr-4 text-right">Bars</th>
                  <th className="pr-4">First</th>
                  <th className="pr-4">Last</th>
                  <th className="pr-4 text-right">Excluded</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {series.map((s) => (
                  <tr key={`${s.symbol}-${s.timeframe}`} className="border-t border-border">
                    <td className="pr-4">{s.symbol}</td>
                    <td className="pr-4">{s.timeframe}</td>
                    <td className="pr-4 text-right">{s.bars.toLocaleString()}</td>
                    <td className="pr-4">{s.first?.slice(0, 16).replace("T", " ")}</td>
                    <td className="pr-4">{s.last?.slice(0, 16).replace("T", " ")}</td>
                    <td className="pr-4 text-right">{(s.excluded ?? 0).toLocaleString()}</td>
                    <td className="whitespace-nowrap">
                      <button
                        onClick={() => void onGaps(s)}
                        className="text-primary underline text-[10px]"
                      >
                        check gaps
                      </button>{" "}
                      <button
                        onClick={() => void onQuality(s)}
                        disabled={busy}
                        className="text-primary underline text-[10px] ml-2"
                      >
                        check quality
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {quality && (
        <div className="flex flex-col gap-1 text-xs font-mono">
          <h3 className="uppercase text-fg-muted">
            Quality of {quality.symbol} {quality.timeframe}
          </h3>
          {quality.would_flag === 0 ? (
            <p className="text-success">
              No bad ticks, stale duplicates or zero-volume spikes found
              {quality.already_excluded ? ` (${quality.already_excluded} already excluded)` : ""}.
            </p>
          ) : (
            <>
              <p>
                Cleaning would exclude {quality.would_flag.toLocaleString()} bar(s):{" "}
                {Object.entries(quality.by_reason)
                  .map(([k, n]) => `${k.replace(/_/g, " ")} ${n}`)
                  .join(", ")}
                . Excluded bars are skipped by backtests, never deleted.
              </p>
              <ul className="text-fg-muted">
                {quality.examples.map((e) => (
                  <li key={e.time + e.reason}>
                    {e.time.slice(0, 16).replace("T", " ")} {e.reason.replace(/_/g, " ")}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="flex gap-2">
            {quality.would_flag > 0 && (
              <button onClick={() => void onClean(quality)} disabled={busy} className={button}>
                Exclude {quality.would_flag} bar(s)
              </button>
            )}
            {quality.already_excluded > 0 && (
              <button onClick={() => void onRestore(quality)} disabled={busy} className={button}>
                Restore {quality.already_excluded} excluded
              </button>
            )}
          </div>
        </div>
      )}

      {gaps && (
        <div className="flex flex-col gap-1 text-xs font-mono">
          <h3 className="uppercase text-fg-muted">
            Gaps in {gaps.symbol} {gaps.timeframe} (weekends excluded)
          </h3>
          {gaps.gaps === 0 ? (
            <p className="text-success">No holes longer than 3 bars.</p>
          ) : (
            <>
              <p>
                {gaps.gaps} hole(s), ~{gaps.missing_bars_estimate.toLocaleString()} bars missing.
                Fill the range again to patch them if MT5 has the data.
              </p>
              <ul>
                {gaps.largest.map((g) => (
                  <li key={g.from}>
                    {g.from.slice(0, 16).replace("T", " ")} → {g.to.slice(0, 16).replace("T", " ")}
                    : {g.missing_bars.toLocaleString()} bars
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
